/**
 * Visibility-context module.
 * Builds the mesh-backed lookup consumed by precise visibility scoring, including
 * bounded surface samples and alias resolution for target lookup.
 */
import * as THREE from 'three'
import type { ViewpointBounds, ViewpointVector3 } from '../generate-jig.types'

/**
 * Weighted sample point taken from a mesh surface for precise visibility tests.
 */
export interface ViewpointVisibilitySample {
  point: ViewpointVector3
  normal: ViewpointVector3
  weight: number
}

/**
 * Visibility-ready representation of a mesh node used by the precise scorer.
 */
export interface ViewpointVisibilityElement {
  id: string
  aliases: string[]
  bounds: ViewpointBounds
  mesh: THREE.Mesh
  samples: ViewpointVisibilitySample[]
}

/**
 * Lookup structure used to resolve target ids and aliases into visibility elements.
 */
export interface ViewpointVisibilityContext {
  elements: ViewpointVisibilityElement[]
  elementMap: Map<string, ViewpointVisibilityElement>
  aliasMap: Map<string, ViewpointVisibilityElement[]>
}

const SAMPLE_LIMIT = 24
const OCCLUDER_MATERIAL = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide })

function normalizeVector(vector: THREE.Vector3): ViewpointVector3 {
  const length = vector.length()
  if (length < 1e-10) {
    return { x: 0, y: 0, z: 1 }
  }

  return {
    x: vector.x / length,
    y: vector.y / length,
    z: vector.z / length,
  }
}

function toBounds(box: THREE.Box3): ViewpointBounds {
  const center = new THREE.Vector3()
  const size = new THREE.Vector3()
  box.getCenter(center)
  box.getSize(size)

  return {
    center: { x: center.x, y: center.y, z: center.z },
    size: { x: size.x, y: size.y, z: size.z },
  }
}

function getUniqueAliases(aliases: string[]): string[] {
  const seen: Record<string, boolean> = {}

  return aliases.filter(function (alias: string) {
    const trimmed = alias ? alias.trim() : ''
    if (!trimmed || seen[trimmed]) {
      return false
    }

    seen[trimmed] = true
    return true
  })
}

function sampleTriangleCentroids(mesh: THREE.Mesh, sampleLimit: number): ViewpointVisibilitySample[] {
  // Weighted triangle centroids give the scorer a cheap approximation of "what parts
  // of the object are actually visible" without raycasting every vertex.
  const geometry = mesh.geometry
  const positionAttribute = geometry.getAttribute('position')
  if (!positionAttribute || positionAttribute.count < 3) {
    return []
  }

  const indexAttribute = geometry.getIndex()
  const matrixWorld = mesh.matrixWorld.clone()
  const v0 = new THREE.Vector3()
  const v1 = new THREE.Vector3()
  const v2 = new THREE.Vector3()
  const edge1 = new THREE.Vector3()
  const edge2 = new THREE.Vector3()
  const normal = new THREE.Vector3()
  const centroid = new THREE.Vector3()
  const samples: ViewpointVisibilitySample[] = []
  const triangleCount = indexAttribute ? Math.floor(indexAttribute.count / 3) : Math.floor(positionAttribute.count / 3)

  for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex++) {
    const getVertexIndex = function (offset: number): number {
      return indexAttribute ? indexAttribute.getX((triangleIndex * 3) + offset) : ((triangleIndex * 3) + offset)
    }

    const i0 = getVertexIndex(0)
    const i1 = getVertexIndex(1)
    const i2 = getVertexIndex(2)

    v0.fromBufferAttribute(positionAttribute, i0).applyMatrix4(matrixWorld)
    v1.fromBufferAttribute(positionAttribute, i1).applyMatrix4(matrixWorld)
    v2.fromBufferAttribute(positionAttribute, i2).applyMatrix4(matrixWorld)

    edge1.subVectors(v1, v0)
    edge2.subVectors(v2, v0)
    normal.crossVectors(edge1, edge2)
    const area = normal.length() * 0.5
    if (area < 1e-8) {
      continue
    }

    centroid.copy(v0).add(v1).add(v2).multiplyScalar(1 / 3)
    samples.push({
      point: { x: centroid.x, y: centroid.y, z: centroid.z },
      normal: normalizeVector(normal),
      weight: area,
    })
  }

  if (samples.length <= sampleLimit) {
    return samples.sort(function (a, b) {
      return b.weight - a.weight
    })
  }

  const sortedSamples = samples.slice().sort(function (a, b) {
    return b.weight - a.weight
  })
  const selectedSamples: ViewpointVisibilitySample[] = []
  const step = (sortedSamples.length - 1) / Math.max(sampleLimit - 1, 1)

  for (let index = 0; index < sampleLimit; index++) {
    selectedSamples.push(sortedSamples[Math.min(sortedSamples.length - 1, Math.round(index * step))])
  }

  return selectedSamples
}

function buildVisibilityMesh(mesh: THREE.Mesh, objectName: string): THREE.Mesh {
  const visibilityMesh = new THREE.Mesh(mesh.geometry, OCCLUDER_MATERIAL)
  visibilityMesh.matrixAutoUpdate = false
  visibilityMesh.matrixWorld.copy(mesh.matrixWorld)
  visibilityMesh.matrix.copy(visibilityMesh.matrixWorld)
  visibilityMesh.userData = {
    ...mesh.userData,
    objectName: objectName,
  }
  visibilityMesh.updateMatrixWorld(true)

  return visibilityMesh
}

function createVisibilityElement(params: {
  id: string
  aliases: string[]
  bounds: ViewpointBounds
  mesh: THREE.Mesh
}): ViewpointVisibilityElement {
  const visibilityMesh = buildVisibilityMesh(params.mesh, params.id)

  return {
    id: params.id,
    aliases: getUniqueAliases(params.aliases),
    bounds: params.bounds,
    mesh: visibilityMesh,
    samples: sampleTriangleCentroids(visibilityMesh, SAMPLE_LIMIT),
  }
}

/**
 * Samples each mesh into a visibility-ready element list consumed by the precise scorer.
 * Each element stores a simplified occluder mesh plus a bounded set of weighted surface
 * samples so visibility quality remains predictable on large models.
 */
export function buildVisibilityContextForMeshNodes(
  meshNodes: THREE.Mesh[],
  objectToFullName: Map<THREE.Object3D, string>
): ViewpointVisibilityContext | null {
  if (!meshNodes || meshNodes.length === 0) {
    return null
  }

  const elements: ViewpointVisibilityElement[] = []

  meshNodes.forEach(function (mesh: THREE.Mesh) {
    const meshBox = new THREE.Box3().setFromObject(mesh)
    if (meshBox.isEmpty()) {
      return
    }

    const id = String(objectToFullName.get(mesh) || mesh.name || '')
    if (!id) {
      return
    }

    elements.push(createVisibilityElement({
      id: id,
      aliases: [id, mesh.name || ''],
      bounds: toBounds(meshBox),
      mesh: mesh,
    }))
  })

  if (elements.length === 0) {
    return null
  }

  const elementMap = new Map<string, ViewpointVisibilityElement>()
  const aliasMap = new Map<string, ViewpointVisibilityElement[]>()

  elements.forEach(function (element: ViewpointVisibilityElement) {
    elementMap.set(element.id, element)
    aliasMap.set(element.id, [element])

    element.aliases.forEach(function (alias: string) {
      const matches = aliasMap.get(alias) || []
      matches.push(element)
      aliasMap.set(alias, matches)
    })
  })

  return {
    elements: elements,
    elementMap: elementMap,
    aliasMap: aliasMap,
  }
}

/**
 * Resolves ids or aliases into visibility elements, but only returns alias matches
 * when that alias maps to exactly one mesh-backed element.
 */
export function resolveVisibilityElements(context: ViewpointVisibilityContext, key: string): ViewpointVisibilityElement[] {
  const exact = context.elementMap.get(key)
  if (exact) {
    return [exact]
  }

  const matches = context.aliasMap.get(key) || []
  return matches.length === 1 ? matches : []
}
